import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { QueturePlayer } from '@quebon/shared-learning'
import type { QuetureApiInterface } from '@quebon/shared-learning'
import { VideoDialog } from '@/components/common/VideoDialog'

interface UnitSpecialSlide {
  url: string
  video: string
}

interface UnitSpecialSectionProps {
  /** Title line 1 (top) — primary blue */
  titlePrimary: string
  /** Title line 2 (bottom) — high black */
  titleHigh: string
  description: string
  descriptionMobileA: string
  descriptionMobileB: string
  note: string
  slides: UnitSpecialSlide[]
}

export function UnitSpecialSection({
  titlePrimary,
  titleHigh,
  description,
  descriptionMobileA,
  descriptionMobileB,
  note,
  slides,
}: UnitSpecialSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const [idx, setIdx] = useState(0)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const total = slides.length
  const slide = slides[idx]

  const prev = () => setIdx((i) => (i - 1 + total) % total)
  const next = () => setIdx((i) => (i + 1) % total)

  const quetureApi = useMemo<QuetureApiInterface>(
    () => ({
      getQuetureInfo: async () => {
        throw new Error('Direct hlsUrl used; getQuetureInfo should not be called')
      },
    }),
    [],
  )

  const openVideo = (index: number) => setOpenIndex(index)
  const closeVideo = () => setOpenIndex(null)

  return (
    <section id="unit-special" data-marketing-section="" className="w-full bg-qb-bg-tertiary py-14 lg:py-24 tracking-[-0.6px]">
      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 flex flex-col items-center gap-14 lg:gap-20">
        {/* Title + description */}
        <div className="flex flex-col items-center gap-6 lg:gap-10 text-center">
          <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
            {/* Primary (blue) first, then high (black) — both desktop and mobile */}
            <span className="block text-qb-primary">{titlePrimary}</span>
            <span className="block mt-1 lg:mt-2 text-qb-text-high">{titleHigh}</span>
          </h2>
          <p className="font-normal text-qb-text-mid text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4]">
            <span className="hidden lg:inline">{description}</span>
            <span className="lg:hidden">
              {descriptionMobileA}
              <br aria-hidden />
              {descriptionMobileB}
            </span>
          </p>
        </div>

        {/* Video player area */}
        {slide && (
          <div className="w-full flex flex-col items-center gap-6">
            <div className="relative w-full flex items-center justify-center lg:gap-2">
              {/* Left chevron — desktop only, sits adjacent to the card */}
              <button
                type="button"
                onClick={prev}
                aria-label={mt('navigation.previousVideo')}
                className="hidden lg:flex shrink-0 w-24 h-24 rounded-full items-center justify-center text-qb-primary/40 hover:text-qb-primary transition-colors"
              >
                <ChevronLeft className="w-20 h-20" strokeWidth={1.5} />
              </button>

              {/* Video card */}
              <div className="bg-white rounded-[16px] lg:rounded-[32px] shadow-[0px_8px_12px_rgba(0,0,0,0.1)] p-2 lg:p-3 w-full max-w-[360px] lg:max-w-[820px]">
                <button
                  type="button"
                  onClick={() => openVideo(idx)}
                  aria-label={mt('media.playUnit', { number: idx + 1 })}
                  className="relative block w-full overflow-hidden rounded-[8px] lg:rounded-[24px] bg-[#707070] group"
                  style={{ aspectRatio: '796/448' }}
                >
                  <img
                    src={slide.url}
                    alt={mt('media.unit', { number: idx + 1 })}
                    className="w-full h-full object-cover"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  >
                    <img
                      src="/marketing/main/icon-play.webp"
                      alt=""
                      className="w-10 h-10 md:w-20 md:h-20 lg:w-[120px] lg:h-[120px] transition-opacity group-hover:opacity-90"
                    />
                  </span>
                </button>
              </div>

              {/* Right chevron — desktop only */}
              <button
                type="button"
                onClick={next}
                aria-label={mt('navigation.nextVideo')}
                className="hidden lg:flex shrink-0 w-24 h-24 rounded-full items-center justify-center text-qb-primary/40 hover:text-qb-primary transition-colors"
              >
                <ChevronRight className="w-20 h-20" strokeWidth={1.5} />
              </button>
            </div>

            {/* Pagination dots */}
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={mt('media.slide', { number: i + 1 })}
                  className={
                    i === idx
                      ? 'w-6 h-2 rounded-full bg-qb-primary'
                      : 'w-2 h-2 rounded-full bg-qb-gray-300'
                  }
                />
              ))}
            </div>

            {/* Note */}
            <p className="font-normal text-qb-text-mid text-[18px] lg:text-[20px] leading-[1.5] text-center px-2">
              {note}
            </p>
          </div>
        )}
      </div>

      {/* Video popup dialog */}
      <VideoDialog
        open={openIndex !== null}
        onOpenChange={(open) => {
          if (!open) closeVideo()
        }}
      >
        {openIndex !== null && slides[openIndex] && (
          <QueturePlayer
        renderMetadataMath
            key={slides[openIndex].video}
            qid={`unit-special-${openIndex + 1}`}
            hlsUrl={slides[openIndex].video}
            thumbnailUrl={slides[openIndex].url}
            quetureApi={quetureApi}
            autoPlay
            allowSkip
          />
        )}
      </VideoDialog>
    </section>
  )
}
